/**
 * Scenario Comparison Panel
 * Side-by-side comparison of saved future closure scenarios
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  getSavedScenarios,
  saveScenario,
  deleteScenario,
  SavedScenario,
} from '../services/futureClosureApi';

interface ScenarioComparisonPanelProps {
  reportId: string;
  currentCalculationId?: string;
  currentResult?: any;
}

const formatCurrency = (val: number | undefined | null): string => {
  if (val == null) return '$0';
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatPct = (val: number | undefined | null): string => {
  if (val == null) return 'N/A';
  return `${val}%`;
};

export default function ScenarioComparisonPanel({
  reportId,
  currentCalculationId,
  currentResult,
}: ScenarioComparisonPanelProps) {
  const [scenarios, setScenarios] = useState<SavedScenario[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveNotes, setSaveNotes] = useState('');
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const loadScenarios = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSavedScenarios(reportId);
      setScenarios(data.scenarios || []);
    } catch (err: any) {
      console.error('Failed to load scenarios:', err);
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  useEffect(() => {
    loadScenarios();
  }, [loadScenarios]);

  const handleSave = async () => {
    if (!currentCalculationId || !saveName.trim()) return;
    try {
      setSaving(true);
      setError(null);
      await saveScenario(reportId, saveName.trim(), currentCalculationId, saveNotes);
      setSuccessMsg(`Scenario "${saveName}" saved`);
      setSaveName('');
      setSaveNotes('');
      setShowSaveForm(false);
      await loadScenarios();
      setTimeout(() => setSuccessMsg(null), 3000);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save scenario');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (scenarioId: string) => {
    try {
      await deleteScenario(reportId, scenarioId);
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.delete(scenarioId);
        return next;
      });
      await loadScenarios();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to delete scenario');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedScenarios = scenarios.filter(s => selectedIds.has(s.id));

  // Quick presets for saving
  const presetNames = ['Conservative', 'Base Case', 'Aggressive', 'Settlement Target'];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
          </svg>
          Scenario Comparison
        </h3>
        {currentCalculationId && (
          <button
            onClick={() => setShowSaveForm(!showSaveForm)}
            className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {showSaveForm ? 'Cancel' : 'Save Current as Scenario'}
          </button>
        )}
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2">
          {error}
        </div>
      )}
      {successMsg && (
        <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-lg px-4 py-2">
          {successMsg}
        </div>
      )}

      {/* Save Form */}
      {showSaveForm && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scenario Name</label>
            <div className="flex gap-2 flex-wrap mb-2">
              {presetNames.map(name => (
                <button
                  key={name}
                  onClick={() => setSaveName(name)}
                  className={`px-2 py-1 text-xs rounded border transition-colors ${
                    saveName === name
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-600 border-gray-300 hover:border-indigo-400'
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={saveName}
              onChange={e => setSaveName(e.target.value)}
              placeholder="Enter scenario name..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
            <input
              type="text"
              value={saveNotes}
              onChange={e => setSaveNotes(e.target.value)}
              placeholder="e.g. For settlement opening position..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>
          <button
            onClick={handleSave}
            disabled={saving || !saveName.trim()}
            className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition-colors"
          >
            {saving ? 'Saving...' : 'Save Scenario'}
          </button>
        </div>
      )}

      {/* Scenarios List */}
      {loading ? (
        <div className="text-center py-6 text-gray-500 text-sm">Loading scenarios...</div>
      ) : scenarios.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <svg className="w-10 h-10 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
          </svg>
          <p className="text-gray-500 text-sm">No saved scenarios yet</p>
          <p className="text-gray-400 text-xs mt-1">Run a calculation and save it as a scenario to begin comparing</p>
        </div>
      ) : (
        <>
          <p className="text-xs text-gray-500">
            Select 2+ scenarios to compare side-by-side. {selectedIds.size > 0 && `(${selectedIds.size} selected)`}
          </p>
          <div className="space-y-2">
            {scenarios.map(s => (
              <div
                key={s.id}
                className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  selectedIds.has(s.id)
                    ? 'border-indigo-500 bg-indigo-50 ring-1 ring-indigo-500'
                    : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
                onClick={() => toggleSelect(s.id)}
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(s.id)}
                  onChange={() => toggleSelect(s.id)}
                  className="h-4 w-4 text-indigo-600 rounded"
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm text-gray-900">{s.name}</span>
                    <span className="text-xs text-gray-400">
                      {new Date(s.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500 mt-0.5">
                    <span>Demand: <span className="font-semibold text-gray-700">{formatCurrency(s.results.total_demand)}</span></span>
                    <span>Aggression: {s.parameters.aggression ?? 'N/A'}</span>
                    <span>Base: {s.parameters.use_best_base ? 'Best' : 'Last'} {s.parameters.base_months}mo</span>
                  </div>
                  {s.notes && <p className="text-xs text-gray-400 mt-0.5 truncate">{s.notes}</p>}
                </div>
                <button
                  onClick={e => { e.stopPropagation(); handleDelete(s.id); }}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete scenario"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Side-by-Side Comparison Table */}
      {selectedScenarios.length >= 2 && (
        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
            </svg>
            Side-by-Side Comparison
          </h4>
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600 w-48">Metric</th>
                  {selectedScenarios.map(s => (
                    <th key={s.id} className="text-right px-4 py-3 font-semibold text-gray-900 min-w-[140px]">
                      {s.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {/* Parameters Section */}
                <tr className="bg-blue-50">
                  <td colSpan={selectedScenarios.length + 1} className="px-4 py-2 text-xs font-bold text-blue-700 uppercase tracking-wider">
                    Parameters
                  </td>
                </tr>
                <ComparisonRow label="Aggression" scenarios={selectedScenarios} getValue={s => s.parameters.aggression?.toString() ?? 'N/A'} />
                <ComparisonRow label="Base Period" scenarios={selectedScenarios} getValue={s => `${s.parameters.use_best_base ? 'Best' : 'Last'} ${s.parameters.base_months ?? 36}mo`} />
                <ComparisonRow label="Closure Duration" scenarios={selectedScenarios} getValue={s => `${s.parameters.closure_duration_months ?? 36} months`} />

                {/* Results Section */}
                <tr className="bg-green-50">
                  <td colSpan={selectedScenarios.length + 1} className="px-4 py-2 text-xs font-bold text-green-700 uppercase tracking-wider">
                    Results
                  </td>
                </tr>
                <ComparisonRow
                  label="Total Demand"
                  scenarios={selectedScenarios}
                  getValue={s => formatCurrency(s.results.total_demand)}
                  highlight
                />
                <ComparisonRow label="Lost Profits" scenarios={selectedScenarios} getValue={s => formatCurrency(s.results.lost_profits)} />
                <ComparisonRow label="Shutdown Costs" scenarios={selectedScenarios} getValue={s => formatCurrency(s.results.shutdown_costs)} />
                <ComparisonRow label="Restart Costs" scenarios={selectedScenarios} getValue={s => formatCurrency(s.results.restart_costs)} />

                {/* Continuation Rates */}
                {selectedScenarios[0]?.results?.continuation_percentages && (
                  <>
                    <tr className="bg-purple-50">
                      <td colSpan={selectedScenarios.length + 1} className="px-4 py-2 text-xs font-bold text-purple-700 uppercase tracking-wider">
                        Expense Continuation Rates
                      </td>
                    </tr>
                    {Object.keys(selectedScenarios[0].results.continuation_percentages).map(key => (
                      <ComparisonRow
                        key={key}
                        label={key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                        scenarios={selectedScenarios}
                        getValue={s => formatPct(s.results.continuation_percentages?.[key])}
                      />
                    ))}
                  </>
                )}

                {/* Delta Row */}
                <tr className="bg-amber-50 border-t-2 border-amber-200">
                  <td className="px-4 py-3 font-bold text-amber-800 text-xs uppercase">
                    Demand Spread
                  </td>
                  <td colSpan={selectedScenarios.length} className="px-4 py-3 text-right font-bold text-amber-800">
                    {(() => {
                      const demands = selectedScenarios.map(s => s.results.total_demand);
                      const min = Math.min(...demands);
                      const max = Math.max(...demands);
                      return `${formatCurrency(min)} - ${formatCurrency(max)} (${formatCurrency(max - min)} spread)`;
                    })()}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function ComparisonRow({
  label,
  scenarios,
  getValue,
  highlight = false,
}: {
  label: string;
  scenarios: SavedScenario[];
  getValue: (s: SavedScenario) => string;
  highlight?: boolean;
}) {
  const values = scenarios.map(s => getValue(s));
  // Find max for highlighting
  const numericValues = scenarios.map(s => {
    const v = getValue(s).replace(/[^0-9.-]/g, '');
    return parseFloat(v) || 0;
  });
  const maxIdx = numericValues.indexOf(Math.max(...numericValues));

  return (
    <tr className={highlight ? 'bg-green-50' : ''}>
      <td className={`px-4 py-2 text-gray-600 ${highlight ? 'font-semibold' : ''}`}>{label}</td>
      {values.map((val, i) => (
        <td
          key={i}
          className={`px-4 py-2 text-right tabular-nums ${
            highlight
              ? 'font-bold text-green-700'
              : i === maxIdx && numericValues[maxIdx] > 0
              ? 'font-semibold text-gray-900'
              : 'text-gray-700'
          }`}
        >
          {val}
        </td>
      ))}
    </tr>
  );
}
